<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import ReleaseConfirmationDialog from '@/components/dialogs/ReleaseConfirmationDialog.vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import {
  BopReleaseConfirmationDataQuery,
  BopReleaseConfirmationDataQueryVariables,
  BopReleaseConfirmationReleaseMutation,
  BopReleaseConfirmationReleaseMutationVariables,
} from '@/generated/graphql'
import NotificationSaved from '@/components/notifications/NotificationSaved.vue'

const props = defineProps<{
  productId: string
  productConfigurationId: string
  bopId: string
  modelValue: boolean
}>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  release: []
}>()

const { t } = useI18n()

const fetchQuery = useQuery<
  BopReleaseConfirmationDataQuery,
  BopReleaseConfirmationDataQueryVariables
>(
  gql`
    query BopReleaseConfirmationData($productId: ID!, $productConfigurationId: ID!, $bopId: ID!) {
      product {
        product(id: $productId) {
          name
        }
        productConfiguration(id: $productConfigurationId) {
          name
        }
        bop(id: $bopId) {
          version
          site {
            id
            name
          }
        }
      }
    }
  `,
  () => ({
    productId: props.productId,
    productConfigurationId: props.productConfigurationId,
    bopId: props.bopId,
  }),
)
const data = computed(() => fetchQuery.result.value?.product)

const notifySaved = ref(false)
const mutation = useMutation<
  BopReleaseConfirmationReleaseMutation,
  BopReleaseConfirmationReleaseMutationVariables
>(gql`
  mutation BopReleaseConfirmationRelease($configId: ID!, $siteId: ID!) {
    product {
      releaseLatestBOPVersion(forProductConfigId: $configId, atSiteId: $siteId) {
        id
      }
    }
  }
`)
function release() {
  mutation
    .mutate({
      configId: props.productConfigurationId,
      siteId: data.value?.bop.site.id as string,
    })
    .then(() => {
      notifySaved.value = true
      emit('update:modelValue', false)
      emit('release')
    })
}
</script>

<template>
  <release-confirmation-dialog
    v-if="!!data"
    :model-value="props.modelValue"
    v-bind="$attrs"
    :title="t('component.bopReleaseConfirmation.title')"
    :message="
      t('component.bopReleaseConfirmation.message', {
        productName: data.product.name,
        productConfigurationName: data.productConfiguration.name,
        siteName: data.bop.site.name,
        bopVersion: data.bop.version,
      })
    "
    :loading="mutation.loading.value"
    @update:model-value="(v) => $emit('update:modelValue', v)"
    @release="release"
  />

  <notification-saved v-model="notifySaved" />
</template>

<style scoped lang="scss"></style>
